import React, { useEffect, useState } from 'react';
import { Header } from '../../components/layout/Header';
import { KPICard } from '../../components/dashboard/KPICard';
import { adminApi, seedApi } from '../../lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { 
    Users, 
    CheckCircle, 
    Clock, 
    FileText,
    ArrowRight,
    Building,
    Mail,
    Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { formatDate } from '../../lib/utils';

export default function AdminDashboard() {
    const [loading, setLoading] = useState(true);
    const [customers, setCustomers] = useState([]);
    const [stats, setStats] = useState({
        total_customers: 0,
        approved_customers: 0,
        pending_approvals: 0,
        total_reports: 0,
    });

    useEffect(() => {
        loadData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = async () => {
        try {
            // Seed data first
            try {
                await seedApi.seedData();
            } catch (e) {
                // Already seeded
            }

            const customersRes = await adminApi.getCustomers();
            setCustomers(customersRes.data);
            
            const approved = customersRes.data.filter(c => c.details?.is_approved).length;
            setStats({
                total_customers: customersRes.data.length,
                approved_customers: approved,
                pending_approvals: customersRes.data.length - approved,
                total_reports: customersRes.data.length * 8, // Approximation
            });
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-brand-red/30 border-t-brand-red rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen" data-testid="admin-dashboard">
            <Header 
                title="Admin Dashboard" 
                subtitle="Manage customers and monitor ABM campaigns"
            />

            <div className="p-8 space-y-8">
                {/* Stats */}
                <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <KPICard 
                        title="Total Customers" 
                        value={stats.total_customers}
                        icon={Users}
                        color="red"
                    />
                    <KPICard 
                        title="Approved" 
                        value={stats.approved_customers}
                        icon={CheckCircle}
                        color="inbound"
                    />
                    <KPICard 
                        title="Pending Approval" 
                        value={stats.pending_approvals}
                        icon={Clock}
                        color="ads"
                    />
                    <KPICard 
                        title="Total Reports" 
                        value={stats.total_reports}
                        icon={FileText}
                    />
                </section>

                {/* Customers List */}
                <section className="card-base p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-heading font-bold text-white">
                            CUSTOMERS
                        </h2>
                        <Link to="/admin/customers">
                            <Button variant="ghost" className="btn-ghost gap-2">
                                View All
                                <ArrowRight className="w-4 h-4" />
                            </Button>
                        </Link>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full data-table">
                            <thead>
                                <tr>
                                    <th className="text-left p-4 text-content-muted">Company</th>
                                    <th className="text-left p-4 text-content-muted">Contact</th>
                                    <th className="text-left p-4 text-content-muted">Status</th>
                                    <th className="text-left p-4 text-content-muted">Created</th>
                                    <th className="text-right p-4 text-content-muted">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customers.map((customer) => (
                                    <tr key={customer.user.id}>
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-brand-red/10 flex items-center justify-center">
                                                    <Building className="w-5 h-5 text-brand-red" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-white">
                                                        {customer.details?.company_name || customer.user.company_name || 'N/A'}
                                                    </p>
                                                    <p className="text-xs text-content-muted">
                                                        {customer.details?.website || 'No website'}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <Mail className="w-4 h-4 text-content-muted" />
                                                <span className="text-sm text-content-secondary">
                                                    {customer.user.email}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            {customer.details?.is_approved ? (
                                                <Badge className="badge badge-success">Approved</Badge>
                                            ) : (
                                                <Badge className="badge badge-warning">Pending</Badge>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-2 text-sm text-content-secondary">
                                                <Calendar className="w-4 h-4" />
                                                {formatDate(customer.user.created_at)}
                                            </div>
                                        </td>
                                        <td className="p-4 text-right">
                                            <Link to={`/admin/customers/${customer.user.id}`}>
                                                <Button variant="ghost" size="sm" className="btn-ghost">
                                                    View
                                                </Button>
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {customers.length === 0 && (
                        <div className="text-center py-12">
                            <Users className="w-12 h-12 text-content-muted mx-auto mb-4" />
                            <p className="text-content-secondary">No customers yet</p>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
